import { DOCUMENT, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { AnimationCallbackEvent, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Injector, afterNextRender, computed, effect, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ActivityEntityName, BulkActionDescriptor, MetadataEntityRecordScope, SectionLayout } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { firstValueFrom } from 'rxjs';
import { EntityDataService, EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { PAGE_COMPONENTS, PageHeaderSize, PageMode } from '@workbuddy/ui/blocks/page';
import { BadgeComponent } from '@workbuddy/ui/components/badge';
import { Command } from '@workbuddy/ui/model';
import { buildEntityBulkActionSettingsPanel, EntityBulkActionExecutor, markPanelCurrentValue, resolveBulkConfirmation, resolveCommandCurrentValue, resolveCommandPrimaryPanelId, resolveEntityBulkActionCommands } from '@workbuddy/ui/v2/blocks/entity.bulk.actions';
import { CommandPaletteContext, CommandPaletteRegistration, CommandPaletteService, useCommandPalette } from '@workbuddy/ui/v2/blocks/command.palette';
import { WorkBuddyDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { EntityInspectors } from '@workbuddy/ui/v2/blocks/entity.inspector';
import { ActionBarComponent, WbActionCommand } from '@workbuddy/ui/v2/components/action.bar';
import { ButtonIconComponent } from '@workbuddy/ui/v2/components/button';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import type { SurfaceBreadcrumb } from '@workbuddy/ui/v2/surface.engine/core/contracts/surface.capabilities';
import { FieldMode } from '@workbuddy/ui/v2/surface.engine/core/contracts/field.binding';
import { CompiledTab } from '@workbuddy/ui/v2/surface.engine/core/runtime/compiled.form';
import { readModelValue } from '@workbuddy/ui/v2/surface.engine/core/runtime/model.path';
import { surfaceContentMaxWidthStyle } from '@workbuddy/ui/v2/surface.engine/core/runtime/surfaces/surface.definition';
import { SurfaceEvent } from '@workbuddy/ui/v2/surface.engine/core/runtime/surface.events';
import { SurfaceContextHandle } from '@workbuddy/ui/v2/surface.engine/core/runtime/surface.context/create.surface.context.options';
import { bindSurfaceActiveTab } from '@workbuddy/ui/v2/surface.engine/core/runtime/view.state/bind.surface.active.tab';
import { WbFieldComponent } from '@workbuddy/ui/v2/surface.engine/core/field/wb.field.component';
import { WbSectionComponent } from '@workbuddy/ui/v2/surface.engine/core/sections/wb.section.component';
import { WbPanelComponent } from '@workbuddy/ui/v2/surface.engine/core/panel/wb.panel.component';
import { WbTabShellComponent, WbTabDeclaration } from '@workbuddy/ui/v2/surface.engine/core/tabs/wb.tab.shell.component';
import { RelationGroupSegmentView, WbRelationGroupComponent } from '@workbuddy/ui/v2/surface.engine/core/tabs/wb.relation.group.component';
import { relationGroupMemoryKey } from '@workbuddy/ui/v2/surface.engine/core/tabs/relation.group.selection';
import { ResolvedSurfaceAction, resolveSurfaceActions, toSurfaceActionCommands } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.action.presenter';
import { resolveSurfaceBanner, resolveSurfaceBreadcrumbs, resolveSurfaceHeaderChip, resolveSurfaceHeaderKind, resolveSurfaceHeaderTag, SurfaceHeaderChip, SurfaceHeaderTag } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.chrome.presenter';
import { resolveSurfaceSaveStatus, surfaceSaveStatusLabel, SurfaceSaveStatus } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.save.status';
import { SectionRenderInfo } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.section.render.info';
import { surfaceCapabilityFields } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.capability.presenter';
import { WbSurfaceCapabilityRegionComponent } from '@workbuddy/ui/v2/surface.engine/core/surface/wb.surface.capability.region.component';
import { buildSectionRenderMap, layoutSurfaceSections, visibleSectionsForPaths, SurfaceSectionLayoutBand } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.section.presenter';
import { WbFormDefinitionTabHostComponent } from '@workbuddy/ui/v2/surface.engine/core/surface/form.definition.tab.host.component';
import { FormDefinitionTabStateStore } from '@workbuddy/ui/v2/surface.engine/core/surface/form.definition.tab.state';
import { countSurfaceTabErrors, createRelationMountCache, isSurfaceTabDirty, resolveCustomTabMount, resolveFormDefinitionTabMount, SurfaceCustomTabMount, SurfaceFormDefinitionTabMount } from '@workbuddy/ui/v2/surface.engine/core/surface/surface.tab.presenter';
import { VIEW_STATE_OWNER, ViewStateBag } from '@workbuddy/ui/v2/view.state';
import { resolveBulkCommandKind } from '@workbuddy/ui/v2/blocks/entity.board/model/bulk.command.groups';
import { commandKeymapEntries, formatChord, humanizeSemanticId, resolveCommandShortcut, SemanticCommandId, ShortcutScope, useShortcuts, useShortcutScope } from '@workbuddy/ui/services/keyboard.shortcuts';
import { buildManageBulkActionArgs, executeManageSurfaceCommand, ManageShortcutTarget, resolveManageShortcutTarget } from './manage.surface.bulk.actions';
import { nextManageHeaderChipsStacked } from './manage.header.chip.layout';
import { resolveManageHeaderIdentifierEditor } from './manage.header.identifier.editor';
import { clampManageLeftRailWidth, clearManageLeftRailWidth, manageLeftColumnDisplayWidth, MANAGE_LEFT_RAIL_DEFAULT_WIDTH, readManageLeftColumnCollapsed, readManageLeftRailWidth, writeManageLeftColumnCollapsed, writeManageLeftRailWidth } from './manage.left.rail.width';
import { MANAGE_SURFACE_LEFT_RAIL } from './manage.surface.left.rail';
import { manageHostBridgeStyles, manageHostStyleId } from './wb.surface.manage.host.theme';

const manageCommandsPaletteId = 'surface-manage.commands';
const manageShortcutScope: ShortcutScope = { kind: 'page', pageId: 'surface-manage' };

// Ready-to-render per-tab info. `sections` for form tabs; `mount` for relation
// boards; `segments` for relation groups; `custom` for tabRenderers; `formDefinition` for child Form surfaces.
interface ManageTabRender {
  readonly tab: CompiledTab;
  readonly sections: readonly SectionRenderInfo[];
  readonly layout: readonly SurfaceSectionLayoutBand[];
  readonly mount: ReturnType<ReturnType<typeof createRelationMountCache>['resolve']>;
  readonly segments?: readonly RelationGroupSegmentView[];
  readonly custom: SurfaceCustomTabMount | undefined;
  readonly formDefinition: SurfaceFormDefinitionTabMount | undefined;
}

// The specialized manage surface host. Full-page entity-record
// layout: a header (breadcrumbs · status tags + action commands), an optional
// banner region, a left summary sidebar (display-only sections) and a tabbed
// main column. Renders form, relation, custom, and formDefinition tabs.
// Nothing app-specific lives here: breadcrumbs, banner, tags, commands and
// boards all arrive as runtime capabilities.
@Component({
  selector: 'wb-surface-manage-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [PAGE_COMPONENTS, RouterLink, NgComponentOutlet, NgTemplateOutlet, BadgeComponent, ActionBarComponent, ButtonIconComponent, WbPanelComponent, WbSectionComponent, WbFieldComponent, WbTabShellComponent, WbFormDefinitionTabHostComponent, WbRelationGroupComponent, WbSurfaceCapabilityRegionComponent],
  templateUrl: './wb.surface.manage.host.component.html',
  styleUrls: ['./wb.surface.manage.host.component.less'],
  providers: [FormDefinitionTabStateStore],
  host: {
    '[attr.data-surface]': '"manage"',
    '[attr.data-active-tab]': '$selectedTab() ?? null',
    '[attr.data-has-header-chips]': '$headerChips().length > 0 ? "true" : null',
    '[attr.data-header-chips-stacked]': '$headerChipsStacked() ? "true" : null',
    '[attr.data-left-collapsed]': '$leftColumnCollapsedView() ? "true" : null',
    '[style.--manage-left-rail-width.px]': '$leftColumnDisplayWidth()',
  },
})
export class WbSurfaceManageHostComponent {

  protected readonly PageMode = PageMode;
  protected readonly PageHeaderSize = PageHeaderSize;
  protected readonly SectionLayout = SectionLayout;
  /** Title tags and labelled chips share this — a large Visibility/Entity next to a small Dynamic reads as a different control. */
  protected readonly headerTagSize = 'small' as const;

  private readonly document = inject(DOCUMENT);
  private readonly isMacPlatform = /Mac|iPod|iPhone|iPad/i.test(this.document.defaultView?.navigator.platform ?? '');
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly rights = inject(RightsService);
  private readonly entityData = inject(EntityDataService);
  private readonly entityDataRaw = inject(EntityDataServiceRaw);
  private readonly confirmService = inject(ConfirmServicePro);
  private readonly bulkActionExecutor = inject(EntityBulkActionExecutor);
  private readonly commandPalette = inject(CommandPaletteService);
  private readonly router = inject(Router);
  private readonly inspectors = inject(EntityInspectors, { optional: true });
  private readonly overlayDialog = inject(WorkBuddyDialogRef, { optional: true });
  private readonly viewStateOwner = inject(VIEW_STATE_OWNER, { optional: true });
  /** Optional: only a host that owns a lifetime provides one. Absent means tab state is discarded. */
  private readonly viewStateBag = inject(ViewStateBag, { optional: true });
  private readonly leftRail = inject(MANAGE_SURFACE_LEFT_RAIL, { optional: true });
  private readonly formDefinitionTabState = inject(FormDefinitionTabStateStore);
  private readonly relationMounts = createRelationMountCache({ injector: this.injector, viewStateBag: this.viewStateBag ?? undefined, logScope: 'surface.host.manage' });
  private commandPaletteRegistration!: CommandPaletteRegistration;

  // Inspector history depth is transient and can be restored without its parent records. The breadcrumb
  // capability is authoritative: three crumbs means root + parent + current record and must stay visible.
  protected readonly $showBreadcrumbs = computed(() => this.viewStateOwner !== 'entity-inspector' || this.$breadcrumbs().length > 2);

  /** Brief “Saved” flash after a patch leaves the surface fully clean. */
  private readonly $savedFlash = signal(false);
  /** Sticky until the user edits again or a later patch fully succeeds. */
  private readonly $persistFailed = signal(false);
  private savedFlashTimer: ReturnType<typeof setTimeout> | undefined;
  private leftRailBodyRect: DOMRect | null = null;
  private headerChipLayoutRo?: ResizeObserver;
  /** True only when title + centered chips + actions no longer fit on one row. */
  protected readonly $headerChipsStacked = signal(false);

  public $handle = input.required<SurfaceContextHandle>({ alias: 'handle' });

  protected readonly $page = computed(() => this.$handle().page);
  protected readonly $compiled = computed(() => this.$handle().compiled);
  protected readonly $model = computed(() => this.$page().$model());
  protected readonly $leftRail = computed(() => this.leftRail && (this.leftRail.visible?.(this.$model()) ?? true) ? this.leftRail : null);
  protected readonly $leftRailInputs = computed(() => this.leftRail?.inputs?.(this.$handle()) ?? {});
  protected readonly $leftRailWidth = signal(readManageLeftRailWidth());
  protected readonly $leftRailResizing = signal(false);
  protected readonly $collapseSummary = computed(() => this.$handle().chrome.collapseSummary);
  protected readonly $leftColumnCollapsed = signal(false);
  protected readonly $leftColumnCollapsedView = computed(() => this.$collapseSummary() && this.$leftColumnCollapsed());
  protected readonly $leftColumnDisplayWidth = computed(() => manageLeftColumnDisplayWidth(this.$leftColumnCollapsedView(), this.$leftRailWidth()));
  private leftColumnEntity: string | null = null;
  protected readonly $contentMaxWidth = computed(() => surfaceContentMaxWidthStyle(this.$handle().sectionLayout.maxWidth));

  // Shared tab selection (?tab= when viewStatePersistence is 'url').
  // isReady avoids NG0950 — required $handle is not set during field init.
  private readonly tabBinding = bindSurfaceActiveTab({
    surfaceId: 'manage',
    getPersistence: () => {
      // `?tab=` is global, not outlet-scoped. A manage surface in a dialog would overwrite the
      // inspector's tab (both often have `details`) and closing the dialog would restore the
      // inspector on the dialog's tab. Dialogs are not navigable — memory only.
      if (this.viewStateOwner === 'entity-dialog' || this.overlayDialog) return 'none';
      return this.$handle().surface.viewStatePersistence ?? 'url';
    },
    getViewState: () => this.$page().viewState,
    isValidTab: id => this.$tabDeclarations().some(t => t.value === id && t.visible !== false),
    defaultTab: () => this.$tabDeclarations().find(t => t.visible !== false)?.value,
    sessionScope: () => (this.$model() as { _id?: string } | undefined)?._id,
    isReady: () => {
      try {
        this.$handle();
        return true;
      } catch {
        return false;
      }
    },
  });

  protected readonly $selectedTab = this.tabBinding.$selectedTab;

  // A relation group keeps its segment beside `?tab=`, so it follows the same rule: dialogs never write the URL.
  protected readonly $relationGroupPersistUrl = computed(() => this.viewStateOwner !== 'entity-dialog' && !this.overlayDialog && (this.$handle().surface.viewStatePersistence ?? 'url') === 'url');
  protected readonly $relationGroupMemoryOwner = computed(() => this.$compiled().entity ?? this.$page().surface);
  protected readonly relationGroupMemoryKey = relationGroupMemoryKey;

  // Surface defaultFieldMode → cascaded surfaceMode for tab (form) sections.
  // Summary sections stay display-only unless they opt into `.editable()`.
  protected readonly $surfaceMode = computed<FieldMode>(() => {
    return this.$handle().surface.defaultFieldMode;
  });

  // Surface-root multi-bind validation errors (`attachTo: { type: 'surface' }`).
  protected readonly $surfaceErrors = computed(() => this.$page().$surfaceErrors());

  // ── Header ────────────────────────────────────────────────────────────────
  protected readonly $identifierLabel = computed<string>(() => {
    const header = this.$compiled().header;
    if (!header) return '';
    const value = readModelValue(this.$model(), header.identifier);
    return value == null ? '' : String(value);
  });

  // A visible breadcrumb trail already names the collection ("Jobs / J-104"); the kind would repeat it.
  protected readonly $kind = computed(() => {

    if (this.$showBreadcrumbs() && this.$breadcrumbs().length > 0) return null;
    return resolveSurfaceHeaderKind(this.$compiled().header?.kind, this.$identifierLabel());
  });

  // Opt-in title editor: requires `header.editable` and a compiled field for the identifier bind.
  // The bind need NOT render anywhere — a surface that renames through the header alone marks it
  // systemHidden, and wb-field falls back to bind-level state when there is no rendered occurrence.
  protected readonly $identifierEditor = computed(() => {

    const compiled = this.$compiled();
    const resolved = resolveManageHeaderIdentifierEditor(compiled.header, compiled.fields, compiled.fieldInstances);
    if (!resolved) return null;
    const page = this.$page();
    return { field: page.field(resolved.bind), metadata: resolved.metadata, instance: resolved.instancePath ? page.fieldInstance(resolved.instancePath) : undefined };
  });

  protected readonly $headerTags = computed<readonly SurfaceHeaderTag[]>(() => {
    const header = this.$compiled().header;
    if (!header) return [];
    const model = this.$model();
    const resolver = this.$handle().resolver;
    const views: SurfaceHeaderTag[] = [];
    for (const tag of header.tags) {
      const view = resolveSurfaceHeaderTag(tag, model, resolver);
      if (view) views.push(view);
    }
    return views;
  });

  protected readonly $headerChips = computed<readonly SurfaceHeaderChip[]>(() => {
    const header = this.$compiled().header;
    if (!header) return [];
    const model = this.$model();
    const resolver = this.$handle().resolver;
    return header.chips.map(chip => resolveSurfaceHeaderChip(chip, model, resolver)).filter((chip): chip is SurfaceHeaderChip => !!chip);
  });

  protected readonly $headerSize = computed(() => this.$headerChips().length > 0 ? PageHeaderSize.Large : PageHeaderSize.Small);

  // ── Breadcrumbs (optional capability) ──────────────────────────────────────
  // First runtime in the chain that declares `breadcrumbs` wins. Resolved once
  // per mount; the returned Signal keeps the trail reactive.
  protected readonly $breadcrumbs = computed<readonly SurfaceBreadcrumb[]>(() => {
    const source = this.$breadcrumbsSource();
    const breadcrumbs = source ? source() : [];
    const identifier = this.$identifierLabel();
    if (!identifier || breadcrumbs.length === 0) return breadcrumbs;
    const last = breadcrumbs[breadcrumbs.length - 1];
    return [...breadcrumbs.slice(0, -1), { ...last, label: identifier }];
  });

  private readonly $breadcrumbsSource = computed(() => resolveSurfaceBreadcrumbs(this.$handle().resolver));

  protected async onBreadcrumbClick(event: MouseEvent, routerLink: string | undefined, index: number, count: number): Promise<void> {

    if (!routerLink || this.viewStateOwner !== 'entity-inspector' || !this.inspectors || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();

    if (index === count - 2 && this.inspectors.$depth() > 1) {
      if (await this.inspectors.back()) return;
    }

    await this.navigateFromInspector(routerLink);
  }

  private async navigateFromInspector(routerLink: string): Promise<void> {

    if (!await this.inspectors?.close()) return;
    if (this.primaryPath(this.router.url) === this.primaryPath(routerLink)) return;
    await this.router.navigateByUrl(routerLink);
  }

  private primaryPath(url: string): string {

    return this.router.parseUrl(url).root.children['primary']?.toString() ?? '';
  }

  // ── Banner (optional capability) ────────────────────────────────────────────
  protected readonly $banner = computed(() => resolveSurfaceBanner(this.$handle().resolver));

  // ── Action commands ─────────────────────────────────────────────────────────
  /** Mount-stable actions; descriptor signals remain reactive without re-resolving capabilities. */
  private readonly $resolvedCommands = computed(() => resolveSurfaceActions(this.$handle(), reference => this.resolveMetadataAction(reference)));

  private readonly $surfaceActionCommands = computed<WbActionCommand[]>(() => [...toSurfaceActionCommands(this.$resolvedCommands())]
    .map(command => ({ ...command, run: () => executeManageSurfaceCommand(command, { executor: this.bulkActionExecutor, injector: this.injector, destroyRef: this.destroyRef }) })));

  /** Primary actions stay visible; the full command palette owns all remaining actions. */
  protected readonly $headerActionCommands = computed<WbActionCommand[]>(() => this.$surfaceActionCommands().filter(command => command.pin !== 'never').map(command => {
    const shortcut = resolveCommandShortcut(command.id);
    return { ...command, pin: 'always', tooltip: shortcut ? `${command.tooltip ?? command.label} · ${formatChord(shortcut.chord, this.isMacPlatform)}` : command.tooltip };
  }));
  protected readonly moreActionsTooltip = `More actions · ${formatChord('$mod+k', this.isMacPlatform)}`;

  /** Patch save chrome: dirty / saving / error / brief saved. Hidden when clean. Folds child formDefinition tabs. */
  protected readonly $saveStatus = computed<SurfaceSaveStatus>(() => {
    const page = this.$page();
    const childDirty = this.formDefinitionTabState.anyDirty();
    const childSaving = this.formDefinitionTabState.anySaving();
    return resolveSurfaceSaveStatus({ dirty: page.$dirty() || childDirty, saving: page.$saving() || childSaving, persistFailed: this.$persistFailed() || this.formDefinitionTabState.anyFailed(), savedFlash: this.$savedFlash() });
  });

  protected readonly $saveStatusLabel = computed(() => surfaceSaveStatusLabel(this.$saveStatus()));

  private readonly $bulkActionCommands = computed<readonly Command[]>(() => {
    const metadata = this.$page().entityMetadata;
    return metadata ? resolveEntityBulkActionCommands(metadata.metadata, MetadataEntityRecordScope.Primary, this.rights, 1) : [];
  });

  private readonly $availableBulkActionCommands = computed<readonly Command[]>(() => {
    const page = this.$page();
    return page.$readonly() || page.$dirty() || page.$saving() || page.$initializing() ? [] : this.$bulkActionCommands();
  });

  protected readonly $canOpenManageCommands = computed(() => this.canOpenManageCommands());

  // ── Section render info (summary sidebar + form-tab sections) ───────────────
  private readonly $sectionRenderInfoByPath = computed(() => {
    return buildSectionRenderMap(this.$handle());
  });
  protected readonly $capabilityFields = computed(() => surfaceCapabilityFields(this.$handle().compiled, this.$sectionRenderInfoByPath(), this.$handle().page));

  protected readonly $summary = computed<readonly SectionRenderInfo[]>(() => {
    const byPath = this.$sectionRenderInfoByPath();
    return visibleSectionsForPaths(this.$compiled().summarySections, byPath, this.$page());
  });

  protected readonly $hasLeftColumn = computed(() => !!this.$leftRail() || this.$summary().length > 0);

  // ── Tabs ──────────────────────────────────────────────────────────────────
  /** Resolves form, relation, relation-group, custom, and formDefinition tab presentation. */
  private readonly $manageTabs = computed<readonly ManageTabRender[]>(() => {
    const handle = this.$handle();
    const byPath = this.$sectionRenderInfoByPath();
    return handle.compiled.tabs.map(tab => {
      if (tab.type === 'relation') {
        return { tab, sections: [], layout: [], mount: this.relationMounts.resolve(tab, handle), custom: undefined, formDefinition: undefined };
      }
      if (tab.type === 'relationGroup') {
        const segments = this.relationMounts.resolveSegments(tab, handle).map(({ segment, mount }) => ({ id: segment.id, title: segment.title, mount }));
        return { tab, sections: [], layout: [], mount: undefined, segments, custom: undefined, formDefinition: undefined };
      }
      if (tab.type === 'custom') {
        return { tab, sections: [], layout: [], mount: undefined, custom: resolveCustomTabMount(tab, handle), formDefinition: undefined };
      }
      if (tab.type === 'formDefinition') {
        return { tab, sections: [], layout: [], mount: undefined, custom: undefined, formDefinition: resolveFormDefinitionTabMount(tab, handle) };
      }
      const sections = visibleSectionsForPaths(tab.sections, byPath, handle.page);
      return { tab, sections, layout: layoutSurfaceSections(sections, handle.sectionLayout.columns, tab.sectionColumns), mount: undefined, custom: undefined, formDefinition: undefined };
    });
  });

  /** formDefinition tabs stay mounted (hidden when inactive) so blur saves survive tab switches. */
  protected readonly $formDefinitionTabs = computed(() => this.$manageTabs().filter(info => info.tab.type === 'formDefinition' && !!info.formDefinition));

  protected readonly $tabDeclarations = computed<readonly WbTabDeclaration[]>(() => {
    const page = this.$page();
    return this.$manageTabs().map(({ tab, sections }) => {
      const tabState = page.tab(tab.path);
      const childState = tab.type === 'formDefinition' ? this.formDefinitionTabState.get(tab.path) : undefined;
      return {
        value: tab.path,
        label: tabState.$title() ?? tab.title,
        visible: !tabState.$hidden(),
        errorCount: countSurfaceTabErrors(page, tab, sections) + (childState?.errorCount ?? 0),
        dirty: isSurfaceTabDirty(page, tab, sections, childState?.dirty ?? false),
      };
    });
  });

  protected onSaveStatusEnter(event: AnimationCallbackEvent): void {

    void this.runSaveStatusAnimation(event, 'enter');
  }

  protected onSaveStatusLeave(event: AnimationCallbackEvent): void {

    void this.runSaveStatusAnimation(event, 'leave');
  }

  private async runSaveStatusAnimation(event: AnimationCallbackEvent, phase: 'enter' | 'leave'): Promise<void> {

    const el = event.target as HTMLElement;
    try {
      if (!el?.animate) return;
      const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
      const duration = reduced ? 90 : phase === 'enter' ? 180 : 150;
      const keyframes: Keyframe[] = phase === 'enter'
        ? [{ opacity: 0, transform: 'translateY(-4px)' }, { opacity: 1, transform: 'translateY(0)' }]
        : [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-4px)' }];
      await el.animate(keyframes, { duration, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)', fill: 'both' }).finished.catch(() => undefined);
    } finally {
      event.animationComplete();
    }
  }

  protected readonly $tabRenderByValue = computed(() => {
    const map = new Map<string, ManageTabRender>();
    for (const info of this.$manageTabs()) map.set(info.tab.path, info);
    return map;
  });

  protected readonly $showTabStrip = computed(() => this.$tabDeclarations().filter(tab => tab.visible !== false).length > 1);
  protected readonly $tabStripPadding = computed(() => this.$handle().chrome.tabStripPadding);

  // Active tab content for the body column — shell keeps the strip sticky in
  // page-body-header; this computed follows host-owned selection (or first visible).
  protected readonly $activeTabInfo = computed<ManageTabRender | undefined>(() => {
    const byValue = this.$tabRenderByValue();
    const declarations = this.$tabDeclarations();
    const selected = this.$selectedTab();
    const active = selected && declarations.some(t => t.value === selected && t.visible !== false)
      ? selected
      : declarations.find(t => t.visible !== false)?.value;
    return active ? byValue.get(active) : undefined;
  });

  protected onSelectedTabChange(value: string | undefined): void {

    this.tabBinding.selectTab(value);
  }

  protected onLeftRailResizeStart(event: PointerEvent): void {

    const handle = event.currentTarget as HTMLElement;
    this.leftRailBodyRect = handle.closest('.page-content-area')?.getBoundingClientRect() ?? null;
    if (!this.leftRailBodyRect) return;
    handle.setPointerCapture(event.pointerId);
    this.$leftRailResizing.set(true);
  }

  protected onLeftRailResizeMove(event: PointerEvent): void {

    if (!this.$leftRailResizing() || !this.leftRailBodyRect) return;
    this.$leftRailWidth.set(clampManageLeftRailWidth(event.clientX - this.leftRailBodyRect.left));
  }

  protected onLeftRailResizeEnd(event: PointerEvent): void {

    if (!this.$leftRailResizing()) return;
    (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
    this.$leftRailResizing.set(false);
    this.leftRailBodyRect = null;
    writeManageLeftRailWidth(this.$leftRailWidth());
  }

  protected onLeftRailResizeReset(): void {

    this.$leftRailWidth.set(MANAGE_LEFT_RAIL_DEFAULT_WIDTH);
    clearManageLeftRailWidth();
  }

  protected toggleLeftColumn(): void {

    if (!this.$collapseSummary()) return;
    const entity = this.$page().entityMetadata?.type ?? this.leftColumnEntity;
    const next = !this.$leftColumnCollapsed();
    this.leftColumnEntity = entity;
    this.$leftColumnCollapsed.set(next);
    writeManageLeftColumnCollapsed(entity, next);
  }

  public constructor() {

    DesignSystem.Mount(this.document, manageHostStyleId, manageHostBridgeStyles);
    this.commandPaletteRegistration = useCommandPalette({ id: manageCommandsPaletteId, priority: 30, enabled: () => this.canOpenManageCommands(), context: () => this.buildManageCommandsPalette() });
    this.bindCommandShortcuts();
    this.destroyRef.onDestroy(() => this.headerChipLayoutRo?.disconnect());
    effect(() => {
      let page: ReturnType<WbSurfaceManageHostComponent['$page']>;
      try {
        page = this.$page();
      } catch {
        return;
      }
      const entity = page.entityMetadata?.type ?? null;
      if (entity === this.leftColumnEntity) return;
      this.leftColumnEntity = entity;
      this.$leftColumnCollapsed.set(this.$collapseSummary() && readManageLeftColumnCollapsed(entity));
    });
    afterNextRender(() => this.bindHeaderChipLayout());
    effect(onCleanup => {
      this.$headerChips();
      const frame = requestAnimationFrame(() => {
        this.observeHeaderChipLayout();
      });
      onCleanup(() => cancelAnimationFrame(frame));
    });

    // Drive save-status flash / error stickiness from the mount event bus.
    // Remount swaps the bus; effect cleanup detaches the previous listeners.
    effect(onCleanup => {
      let page: ReturnType<WbSurfaceManageHostComponent['$page']>;
      try {
        page = this.$page();
      } catch {
        return;
      }
      const unsubs = [
        page.eventBus.on(SurfaceEvent.PatchFailed, () => this.$persistFailed.set(true)),
        page.eventBus.on(SurfaceEvent.FieldChange, () => this.$persistFailed.set(false)),
        page.eventBus.on(SurfaceEvent.AfterPatch, () => {
          if (page.$dirty()) return;
          this.$persistFailed.set(false);
          this.$savedFlash.set(true);
          if (this.savedFlashTimer) clearTimeout(this.savedFlashTimer);
          this.savedFlashTimer = setTimeout(() => {
            this.$savedFlash.set(false);
            this.savedFlashTimer = undefined;
          }, 1500);
        }),
      ];
      onCleanup(() => {
        for (const unsub of unsubs) unsub();
        if (this.savedFlashTimer) {
          clearTimeout(this.savedFlashTimer);
          this.savedFlashTimer = undefined;
        }
      });
    });
  }

  protected openManageCommands(): void {

    void this.commandPaletteRegistration.open();
  }

  // Chords come only from the central keymap: the host never names a key, it just binds whatever
  // the map declares to the same target the palette would dispatch.
  private bindCommandShortcuts(): void {

    useShortcutScope(manageShortcutScope);
    useShortcuts(commandKeymapEntries().map(([semanticId, keys]) => ({
      id: `manage.${semanticId}`,
      keys,
      label: humanizeSemanticId(semanticId),
      scope: manageShortcutScope,
      when: () => this.canOpenManageCommands() && !this.commandPalette.isOpen() && !!this.resolveShortcutTarget(semanticId),
      action: () => this.runShortcutTarget(semanticId),
    })));
  }

  /** Resolved fresh on every keystroke — both command lists are signal-derived and change with the record. */
  private resolveShortcutTarget(semanticId: SemanticCommandId): ManageShortcutTarget | null {

    return resolveManageShortcutTarget(semanticId, this.$surfaceActionCommands(), this.$availableBulkActionCommands());
  }

  private runShortcutTarget(semanticId: SemanticCommandId): void {

    const target = this.resolveShortcutTarget(semanticId);
    if (!target) return;
    if (target.kind === 'surface') {
      void target.command.run?.();
      return;
    }
    if (target.kind === 'bulk-action') {
      this.executeBulkAction(target.command);
      return;
    }
    // Deep-link straight into the command's value panel — landing on the palette root would only
    // highlight the row and leave the user to click through to the picker the chord already named.
    const initialPanelId = resolveCommandPrimaryPanelId(target.command) ?? undefined;
    void this.commandPaletteRegistration.open({ initialPanelId, initialPanelTitle: initialPanelId ? target.command.label : undefined });
  }

  private bindHeaderChipLayout(): void {

    this.headerChipLayoutRo?.disconnect();
    this.headerChipLayoutRo = new ResizeObserver(() => this.updateHeaderChipLayout());
    this.observeHeaderChipLayout();
  }

  private observeHeaderChipLayout(): void {

    const header = this.managePageHeader();
    if (!header || !this.headerChipLayoutRo) return;
    this.headerChipLayoutRo.disconnect();
    this.headerChipLayoutRo.observe(header);
    for (const el of [header.querySelector('.manage-identity'), header.querySelector('.manage-header-chips'), header.querySelector('.manage-header-actions')]) {
      if (el) this.headerChipLayoutRo.observe(el);
    }
  }

  private updateHeaderChipLayout(): void {

    const header = this.managePageHeader();
    const chips = header?.querySelector<HTMLElement>('.manage-header-chips');
    if (!header || !chips || this.$headerChips().length === 0) {
      if (this.$headerChipsStacked()) this.$headerChipsStacked.set(false);
      return;
    }

    const identity = header.querySelector<HTMLElement>('.manage-identity');
    const actions = header.querySelector<HTMLElement>('.manage-header-actions');
    const style = getComputedStyle(header);
    const stacked = nextManageHeaderChipsStacked({
      headerWidth: header.clientWidth,
      paddingLeft: parseFloat(style.paddingLeft) || 0,
      paddingRight: parseFloat(style.paddingRight) || 0,
      columnGap: parseFloat(style.columnGap) || 16,
      chipsWidth: measureHeaderClusterWidth(chips),
      // Identity grows (`flex: 1 1 auto`) so a long name can ellipsis, and the stacked
      // grid column is `1fr`. Collision math needs the title/tags cluster — same
      // content-sum as chips/actions — not that grown box. `scrollWidth` latches stacked.
      identityWidth: identity ? measureHeaderClusterWidth(identity) : 0,
      actionsWidth: actions ? measureHeaderClusterWidth(actions) : 0,
    }, this.$headerChipsStacked());
    if (this.$headerChipsStacked() !== stacked) this.$headerChipsStacked.set(stacked);
  }

  private managePageHeader(): HTMLElement | null {

    return this.host.nativeElement.querySelector(':scope > w-page.surface-manage-frame > .page-header');
  }

  private canOpenManageCommands(): boolean {

    const page = this.$page();
    const surfaceActions = this.$surfaceActionCommands();
    const hasEnabledAction = surfaceActions.some(command => !command.disabled);
    return !page.$initializing() && (hasEnabledAction || this.$availableBulkActionCommands().length > 0) && typeof this.$model()._id === 'string' && (this.viewStateOwner === 'entity-inspector' || !this.inspectors?.$activeInspector());
  }

  private buildManageCommandsPalette(): CommandPaletteContext | null {

    if (!this.canOpenManageCommands()) return null;
    const surfaceCommands = this.$surfaceActionCommands().map(command => this.toPaletteCommand(command));
    const surfaceIds = new Set(surfaceCommands.map(command => command._id));
    const metadataCommands = this.$availableBulkActionCommands().filter(command => !surfaceIds.has(command._id));
    const metadataActions = metadataCommands.filter(command => this.bulkCommandKind(command) === 'action');
    const metadataFields = metadataCommands.filter(command => this.bulkCommandKind(command) === 'field').map(command => this.markCurrentValue(command));
    const metadataCustomFields = metadataCommands.filter(command => this.bulkCommandKind(command) === 'customField').map(command => this.markCurrentValue(command));

    return {
      id: manageCommandsPaletteId,
      scopeChip: { label: this.$identifierLabel() || 'Current record' },
      settingsPanel: buildEntityBulkActionSettingsPanel({
        title: 'Commands',
        groups: [
          { label: 'Actions', commands: [...surfaceCommands, ...metadataActions] },
          { label: 'Set Fields', commands: metadataFields },
          { label: 'Set Additional Fields', commands: metadataCustomFields },
        ],
        execute: command => this.executeManageCommand(command),
        cancel: () => this.commandPalette.close(),
      }),
    };
  }

  /**
   * One record is mounted, so every field command has exactly one current value — mark it on the
   * command's value panel so the picker opens showing what the record already holds. The board's
   * palette never does this: a multi-record selection has no single current value.
   */
  private markCurrentValue(command: Command): Command {

    const panel = command.data?.commandPalette?.settingsPanel;
    if (!panel) return command;

    const descriptor = (command.data as { bulkActionDescriptor?: BulkActionDescriptor } | undefined)?.bulkActionDescriptor;
    const marked = markPanelCurrentValue(panel, resolveCommandCurrentValue(descriptor, this.$model()));
    return marked === panel ? command : command.clone({ data: { ...command.data, commandPalette: { ...command.data.commandPalette, settingsPanel: marked } } });
  }

  private resolveMetadataAction(reference: string): ResolvedSurfaceAction | undefined {

    const command = this.$bulkActionCommands().find(candidate => candidate._id === reference);
    if (!command) return undefined;
    const page = this.$page();
    return {
      descriptor: computed(() => ({
        id: command._id,
        label: command.label,
        iconLeft: command.iconLeft,
        hidden: page.$readonly() || command.hidden,
        disabled: page.$dirty() || page.$saving() || command.disabled,
        tooltip: command.tooltip,
        pin: 'preferred',
      })),
      run: () => this.executeBulkAction(command),
    };
  }

  private toPaletteCommand(command: WbActionCommand): Command {

    return new Command({ _id: command.id, label: command.label, iconLeft: command.iconLeft, disabled: command.disabled ?? false, tooltip: command.tooltip, data: { commandPalette: { settingsPanel: { kind: 'action' } } } });
  }

  private bulkCommandKind(command: Command): ReturnType<typeof resolveBulkCommandKind> {

    const descriptor = (command.data as { bulkActionDescriptor?: BulkActionDescriptor } | undefined)?.bulkActionDescriptor;
    return resolveBulkCommandKind(descriptor);
  }

  private executeManageCommand(command: Command): void {

    const surfaceCommand = this.$surfaceActionCommands().find(candidate => candidate.id === command._id);
    if (surfaceCommand) {
      this.commandPalette.close();
      void surfaceCommand.run?.();
      return;
    }
    this.executeBulkAction(command);
  }

  private executeBulkAction(command: Command): void {

    const metadata = this.$page().entityMetadata;
    if (!metadata) return;
    const entity = metadata.type as ActivityEntityName;
    const model = this.$model();
    const args = buildManageBulkActionArgs(entity, model, command);
    if (!args) return;
    const recordId = String(model._id);
    const descriptor = (command.data as { bulkActionDescriptor?: BulkActionDescriptor } | undefined)?.bulkActionDescriptor;
    const confirmation = resolveBulkConfirmation(descriptor, 1, metadata.metadata.displayName);

    this.commandPalette.close();
    void this.bulkActionExecutor.execute({ context: 'record', entity, args, injector: this.injector, destroyRef: this.destroyRef, confirm: () => confirmation ? this.confirmService.confirm(confirmation.title, confirmation.body, 'danger') : true, onCompleted: () => void this.reloadBulkActionRecord(entity, recordId) });
  }

  private async reloadBulkActionRecord(entity: ActivityEntityName, recordId: string): Promise<void> {

    this.entityData.invalidate(entity, [entity, 'item', recordId]);
    if (this.destroyRef.destroyed) return;

    try {
      const response = await firstValueFrom(this.entityDataRaw.getById(entity, recordId));
      if (!this.destroyRef.destroyed && !this.$page().$dirty() && String(this.$model()._id) === recordId && response.item) this.$handle().reset(response.item as Record<string, unknown>);
    } catch {
      // Cache invalidation still ensures the next mount receives fresh data.
    }
  }

}

// Sums the laid-out width of a header cluster. Irreducibly DOM-bound: every term is a live
// computed-style / layout read, so there is no arithmetic left to lift out.
function measureHeaderClusterWidth(el: HTMLElement): number {

  const style = getComputedStyle(el);
  const gap = parseFloat(style.columnGap || style.gap) || 0;
  let width = 0;
  let visible = 0;
  for (const child of Array.from(el.children)) {
    if (!(child instanceof HTMLElement) || getComputedStyle(child).display === 'none') continue;
    if (child.tagName.toLowerCase() === 'wb-action-bar') {
      const barStyle = getComputedStyle(child);
      width += measureActionBarWidth(child, parseFloat(barStyle.columnGap || barStyle.gap) || 0);
    } else {
      width += child.offsetWidth;
    }
    visible += 1;
  }
  return visible > 1 ? width + gap * (visible - 1) : width;
}

// `gap` comes from the caller's computed-style read of this bar, so the bar's own laid-out gap is the
// single source (it previously hardcoded 8 while the caller measured the real value).
function measureActionBarWidth(bar: HTMLElement, gap: number): number {

  const measure = bar.querySelector('.wb-action-bar-measure');
  if (measure instanceof HTMLElement && measure.scrollWidth > 0) return measure.scrollWidth;
  let width = 0;
  let visible = 0;
  for (const child of Array.from(bar.children)) {
    if (!(child instanceof HTMLElement) || child.classList.contains('wb-action-bar-measure') || getComputedStyle(child).display === 'none') continue;
    width += child.offsetWidth;
    visible += 1;
  }
  return visible > 1 ? width + gap * (visible - 1) : width;
}
